import React, { useRef } from 'react';
import { Button, message, Popconfirm, Tag } from 'antd';
// import type { ColumnsType } from 'antd/es/table/interface';
import type { ActionType, ProColumns } from '@ant-design/pro-table';
import { ProTable } from '@ant-design/pro-table';
import './index.less';
import {
  ClockCircleOutlined,
  MinusCircleOutlined,
  IssuesCloseOutlined,
  SyncOutlined,
  CloudUploadOutlined,
  CloudSyncOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';

import {
  getGroupList,
  getProviderListAll,
  getEcsList,
  deleteEcs,
  recycleEcs,
} from '@/services/flexGroup';

export interface EcsType {
  id: string;
  firstWorkTime: string;
  groupId: string;
  groupName: string;
  instanceStatus: string;
  createTime: string;
  ip: string;
  name: string;
  providerId: string;
  providerName: string;
  providerType: string;
  lastWorkTime: string;
  status: string;
  statusName: string;
  readyTime: string;
  recycledTime: string;
}

interface SearchType {
  name: string;
  status: string;
  provider: string;
}

// const data = new Array<GroupsType>(20).fill(2 as any).map((item, index) => ({
//     id: 'x' + index,
//     max: 6,
//     current: Number((6 - 20 / (index + 5)).toFixed(0)),
//     abortStrategy: 'aaa',
//     creatTime: '2022-10-1',
//     expected: 3,
//     name: 'ccc' + index,
//     provider: 'ali cloud',
//     providerName: 'ali cloud',
//     standBy: 1,
//     status: 'working',
//     userId: 'aaa',
//     userName: 'Ballack',
//     workStatusCallbackUrl: 'https://transfer-dev.api.patternx.gemdale.com//v1/transfer/ecs/callback'
// }))
const tags: any = {
  creating: (
    <Tag icon={<CloudUploadOutlined />} color="success">
      创建中
    </Tag>
  ),
  working: (
    <Tag icon={<SyncOutlined spin />} color="processing">
      工作中
    </Tag>
  ),
  waiting: (
    <Tag icon={<ClockCircleOutlined />} color="geekblue">
      等待中
    </Tag>
  ),
  recycling: (
    <Tag icon={<CloudSyncOutlined />} color="warning">
      回收中
    </Tag>
  ),
  recycled: (
    <Tag icon={<MinusCircleOutlined />} color="default">
      已回收
    </Tag>
  ),
  holding: (
    <Tag icon={<IssuesCloseOutlined />} color="cyan">
      保留中
    </Tag>
  ),
  shutdown: (
    <Tag icon={<CloseCircleOutlined />} color="red">
      已关机
    </Tag>
  ),
};

const Ecs: React.FC = () => {
  const actionRef = useRef<ActionType>();

  const onDelete = (ids: string[]) => {
    deleteEcs(ids).then((res) => {
      if (res?.data.code === 'SUCCESS') {
        message.success('删除成功');
        actionRef.current?.reload();
        actionRef.current?.clearSelected && actionRef.current?.clearSelected();
      }
    });
  };
  const onRecycle = (ids: string[]) => {
    recycleEcs(ids).then((res) => {
      if (res?.data.code === 'SUCCESS') {
        message.success('回收成功');
        actionRef.current?.reload();
        actionRef.current?.clearSelected && actionRef.current?.clearSelected();
      }
    });
  };

  const columns: ProColumns<EcsType>[] = [
    {
      title: 'id',
      dataIndex: 'id',
      valueType: 'text',
      search: false,
    },
    {
      title: '名称',
      dataIndex: 'name',
      valueType: 'text',
      colSize: 1,
    },
    {
      title: '状态',
      dataIndex: 'statusName',
      valueType: 'text',
      colSize: 1.5,
      formItemProps: { name: 'ecsStatusList' },
      valueEnum: {
        creating: { text: '创建中' },
        working: { text: '工作中' },
        waiting: { text: '等待中' },
        recycling: { text: '回收中' },
        recycled: { text: '已回收' },
        holding: { text: '保留中' },
        shutdown: { text: '已关机' },
      },
      fieldProps: { mode: 'multiple' },
      render: (_, entity) => {
        console.log('statusName', _, entity);
        return tags[entity.status];
      },
      // width: 80
    },
    {
      title: '伸缩组名称',
      dataIndex: 'groupName',
      formItemProps: { name: 'groupId' },
      colSize: 1,
      request: () => {
        return getGroupList().then((res) => {
          if (res) {
            const record = res.data.records;
            return Promise.resolve(
              record.map((item) => ({
                label: item.name,
                value: item.id,
              })),
            );
          }
          return Promise.resolve([]);
        });
      },
    },
    {
      title: '云商名称',
      dataIndex: 'providerName',
      formItemProps: { name: 'providerId' },
      colSize: 1.5,
      request: () => {
        return getProviderListAll().then((res) => {
          if (res) {
            const record = res.data.records;
            return Promise.resolve(
              record.map((item) => ({
                label: item.name,
                value: item.id,
              })),
            );
          }
          return Promise.resolve([]);
        });
      },
    },
    {
      title: 'ip',
      dataIndex: 'ip',
      valueType: 'text',
      search: false,
    },
    {
      title: '在云商的原始状态',
      dataIndex: 'instanceStatus',
      valueType: 'text',
      search: false,
    },
    {
      title: '首次工作时间',
      dataIndex: 'firstWorkTime',
      valueType: 'text',
      search: false,
    },
    {
      title: '末次工作时间',
      dataIndex: 'lastWorkTime',
      valueType: 'text',
      search: false,
    },
    {
      title: '就绪时间',
      dataIndex: 'readyTime',
      search: false,
      // width: 120,
    },
    {
      title: '回收时间',
      dataIndex: 'recycledTime',
      search: false,
      // width: 120,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      search: false,
    },
    {
      title: '最后开机时间',
      dataIndex: 'lastStartUpTime',
      search: false,
    },
    {
      title: '最后关机时间',
      dataIndex: 'lastShutdownTime',
      search: false,
    },
    {
      title: '服务版本',
      dataIndex: 'version',
      search: false,
    },
    {
      title: '镜像id',
      dataIndex: 'imageId',
      search: false,
    },
    {
      title: 'groupSignature',
      dataIndex: 'groupSignature',
      search: false,
    },
    {
      title: '操作',
      valueType: 'option',
      fixed: 'right',
      render: (_, entity) => [
        <Popconfirm
          title={'确定删除吗?'}
          onConfirm={() => onDelete([entity.id])}
          key="stop"
          disabled={entity.status !== 'recycled'}
        >
          <div className={`optionButton ${entity.status !== 'recycled' ? 'disabled' : null}`}>
            {'删除'}
          </div>
        </Popconfirm>,
        <Popconfirm
          title={'确定回收吗?'}
          onConfirm={() => onRecycle([entity.id])}
          key="recycle"
          disabled={entity.status === 'recycled'}
        >
          <div className={`optionButton ${entity.status === 'recycled' ? 'disabled' : null}`}>
            强制回收
          </div>
        </Popconfirm>,
      ],
    },
  ];

  const onDeletes = (rows: EcsType[]) => {
    onDelete(rows.map((item) => item.id));
  };

  const onRecycles = (rows: EcsType[]) => {
    onRecycle(rows.map((item) => item.id));
  };

  const canDelete = (rows: EcsType[] | undefined) => {
    return !rows?.length;
  };

  const canRecycle = (rows: EcsType[] | undefined) => {
    return !rows?.length;
  };

  const rowSelection = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: EcsType[]) => {},
  };

  // getGroupList({}).then(res => {
  //     console.log(res)
  // })

  return (
    <div className="flexGroup">
      <ProTable<EcsType, SearchType>
        columns={columns}
        defaultSize="large"
        scroll={{ x: 'max-content' }}
        rowSelection={{}}
        rowKey={(record) => record.id}
        tableLayout={'auto'}
        actionRef={actionRef}
        toolBarRender={(_, rows) => [
          <Button
            key="add"
            type="primary"
            onClick={() => onDeletes(rows.selectedRows || [])}
            disabled={canDelete(rows.selectedRows)}
          >
            批量删除
          </Button>,
          <Button
            key="recycle"
            type="primary"
            onClick={() => onRecycles(rows.selectedRows || [])}
            disabled={canRecycle(rows.selectedRows)}
          >
            批量强制回收
          </Button>,
        ]}
        pagination={{
          pageSizeOptions: ['10', '20', '30'],
          defaultPageSize: 10,
          showSizeChanger: true,
        }}
        search={{ span: 4 }}
        request={async (
          params: SearchType & {
            current?: number | undefined;
            pageSize?: number | undefined;
            pageIndex?: number | undefined;
          },
          sort,
          filter,
        ) => {
          params.pageIndex = params.current;
          const data = await getEcsList(params);
          return {
            data: data?.data.records,
            success: data?.success,
            total: data?.data.total,
          };
        }}
      />
    </div>
  );
};

export default Ecs;
